import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AudioOutlined, CaretRightFilled, LoadingOutlined } from "@ant-design/icons";
import { clockOf } from "../rules/format";
import { useReviewClock } from "../clock";
import { liveSpan, toneOf } from "../constants";
import ChunkedPlayer, { clipIndexAt, gapsOf } from "./ChunkedPlayer";
import Scrubber from "./Scrubber";
import Controls from "./Controls";

/*
 * The recording, on the room's clock. The camera or the screen recording is
 * the picture (a switch between them keeps the moment); where the round has an
 * audio recording, it is the sound, kept in step with the picture, and the
 * video is muted (whether camera chunks carry sound is not known). A round with
 * only audio plays the audio as the recording.
 *
 * @param {Object} props
 * @param {Array<{k: string, n: string, clips: Array, gaps?: Array}>} props.sources  video recordings with clips
 * @param {{clips: Array, gaps?: Array}|null} props.audio
 * @param {Array} props.modules
 * @param {Function} props.moduleAt
 * @param {Array} props.flagged
 * @param {Array} [props.restarts]  session restarts, marked on the scrubber
 * @param {Function} props.onFlag      a pin or prev/next: (item) => void
 * @param {Function} props.onMediaError (kind) => void
 * @param {boolean} props.loading
 */
export default function Player({ sources, audio, modules, moduleAt, flagged, restarts = [], onFlag, onMediaError, loading = false }) {
  const c = useReviewClock();
  /* the camera first (sources come in that order), until the reviewer picks one; the media load in any order */
  const [chosen, setChosen] = useState(null);
  const source = sources.some((s) => s.k === chosen) ? chosen : (sources[0] ? sources[0].k : null);
  const video = sources.find((s) => s.k === source) || null;
  const audioClips = (audio && audio.clips) || [];
  const hasCompanion = !!video && audioClips.length > 0;
  const mainClips = video ? video.clips : audioClips;
  const mainKind = video ? video.k : "audio";

  const mainRef = useRef(null);
  const compRef = useRef(null);
  const lastSync = useRef(-10);

  /* the picture (or the audio alone) drives the clock; the sound follows */
  const { register, report, reportEnded } = c;
  useEffect(() => {
    const main = mainRef.current;
    if (!main || !mainClips.length) return undefined;
    const comp = hasCompanion ? compRef.current : null;
    return register({
      seek: (t) => { main.seek(t); if (comp) { comp.seek(t); lastSync.current = t; } },
      play: () => { main.play(); if (comp) comp.play(); },
      pause: () => { main.pause(); if (comp) comp.pause(); },
      setRate: (r) => { main.setRate(r); if (comp) comp.setRate(r); },
    });
  }, [register, mainKind, hasCompanion, mainClips.length]);

  const onTime = useCallback((t) => {
    report(t);
    if (hasCompanion && compRef.current && Math.abs(t - lastSync.current) >= 0.5) {
      lastSync.current = t;
      compRef.current.sync(t);
    }
  }, [report, hasCompanion]);

  const m = moduleAt(c.t);
  const mi = m && !m.pseudo ? modules.indexOf(m) : -1;
  const gaps = useMemo(() => (video && video.gaps && video.gaps.length ? video.gaps : gapsOf(mainClips)), [video, mainClips]);
  const first = mainClips.length ? mainClips[0] : null;
  const last = mainClips.length ? mainClips[mainClips.length - 1] : null;
  const beforeStart = !!first && c.t < first.from - 0.5;
  const afterEnd = !!last && c.t > last.to + 0.5;
  const inGap = !!first && !beforeStart && !afterEnd && clipIndexAt(mainClips, c.t).gap;
  const live = c.engaged ? flagged.filter((x) => c.near(x.at, liveSpan(x))) : [];

  return (
    <div className="proctoring-review__player" id="proctoring-review-player" data-source={mainKind}>
      <div className="proctoring-review__screen" onClick={c.toggle} role="presentation">
        {mainClips.length > 0 && (
          <ChunkedPlayer
            key={mainKind}
            ref={mainRef}
            clips={mainClips}
            kind={video ? "video" : "audio"}
            muted={hasCompanion}
            onTime={onTime}
            onEnded={reportEnded}
            onError={() => onMediaError(mainKind)}
            label={video ? `${video.n} recording` : "Audio recording"}
          />
        )}
        {hasCompanion && (
          <ChunkedPlayer
            ref={compRef}
            clips={audioClips}
            kind="audio"
            follow
            onError={() => onMediaError("audio")}
            label="Audio recording"
          />
        )}
        {!video && (
          <div className="proctoring-review__audio-only" aria-hidden="true">
            <AudioOutlined />
            <span>Audio recording</span>
          </div>
        )}
        {m && (
          <span className={`proctoring-review__now-module${m.pseudo ? " proctoring-review__now-module--none" : ""}`} style={m.pseudo ? undefined : { background: toneOf(mi).pill }}>
            {m.name}
          </span>
        )}
        <span className="proctoring-review__now-time">{`${clockOf(c.t)} / ${clockOf(c.length)}`}</span>
        {loading && <span className="proctoring-review__screen-note"><LoadingOutlined /> Loading the recording</span>}
        {!loading && !mainClips.length && <span className="proctoring-review__screen-note">The recording could not be loaded</span>}
        {!loading && beforeStart && <span className="proctoring-review__screen-note">{`The recording starts at ${clockOf(first.from)}`}</span>}
        {!loading && afterEnd && <span className="proctoring-review__screen-note">{`The recording ended at ${clockOf(last.to)}`}</span>}
        {!loading && inGap && (
          <span className="proctoring-review__screen-note">Nothing was recorded here; playback skips to the next recorded moment</span>
        )}
        {!c.playing && mainClips.length > 0 && <span className="proctoring-review__big-play" aria-hidden="true"><CaretRightFilled /></span>}
        <div className="proctoring-review__live-flags" aria-live="polite">
          {live.map((x) => (
            <span key={x.key} className={`proctoring-review__live-flag${x.severity === "critical" ? " proctoring-review__live-flag--critical" : ""}`}>{x.what}</span>
          ))}
        </div>
      </div>
      <Scrubber
        length={c.length}
        t={c.t}
        modules={modules}
        gaps={gaps}
        flagged={flagged}
        restarts={restarts}
        onSeek={(at, o) => c.seek(at, o)}
        onPin={onFlag}
      />
      <Controls
        t={c.t}
        playing={c.playing}
        rate={c.rate}
        modules={modules}
        current={m}
        flagged={flagged}
        sources={sources}
        source={source}
        onSource={setChosen}
        onToggle={c.toggle}
        onSeek={(at) => c.seek(at)}
        onFlag={onFlag}
        onRate={c.setRate}
      />
    </div>
  );
}
